import { Box } from 'common/src/designSystem/components/box';
import { Flex } from 'common/src/designSystem/components/flex';
import { Spacer } from 'common/src/designSystem/components/spacer';
import {
    Campaign,
    CampaignId,
    DelegationId,
    EventId,
    MassActionSelectedsInput,
    OrganizationEventLevel,
    OrganizationId
} from 'common/src/generated/types';
import { isNonEmptyArray } from 'common/src/util/array';
import { encodeBase64 } from 'common/src/util/base64';
import { Emptyable } from 'common/src/util/emptyable';
import { CampaignsPaths } from 'common/src/util/paths/campaignsPaths';
import * as React from 'react';
import { Button } from '../../designSystem/components/button';
import { IconBackground } from '../../designSystem/components/iconBackground';
import { Modal } from '../../designSystem/components/modal';
import { RichSelect } from '../../designSystem/components/richSelect/richSelect';
import {
    useMassCampaignSendCampaignsQuery,
    useUsersInfosMassCampaignSendMutation
} from '../../generated/graphqlHooks';
import { useHeavent } from '../../hooks/useHeavent';

interface IMassCampaignSendModalProps {
    campaigns: Array<Pick<Campaign, 'id' | 'name'>>;
    canCreate: boolean;
    delegationId: Emptyable<DelegationId>;
    eventId: Emptyable<EventId>;
    organizationId: OrganizationId;
    selecteds: MassActionSelectedsInput;

    onClose(): void;
    onSuccess(): void;
}

export const MassCampaignSendModal = (props: IMassCampaignSendModalProps) => {
    const { history, translate } = useHeavent();
    const { mutate: massCampaignSend, isLoading: isMassCampaignSendLoading } =
        useUsersInfosMassCampaignSendMutation();
    const [campaignsIds, setCampaignsIds] = React.useState<CampaignId[]>([]);
    const valid = isNonEmptyArray(campaignsIds);
    const onUpdate = async () => {
        if (isNonEmptyArray(campaignsIds)) {
            await massCampaignSend({
                organizationId: props.organizationId,
                eventId: props.eventId,
                delegationId: props.delegationId,
                selecteds: props.selecteds,
                campaignId: campaignsIds[0]
            });

            props.onSuccess();
            props.onClose();
        }
    };

    return (
        <Modal size="md" onClose={props.onClose}>
            <Flex justify="center">
                <IconBackground color="primary" icon="people-group" />
            </Flex>

            <Spacer height="4" />

            <Box font="gray900 textLg medium" textAlign="center">
                {translate('SendCampaign')}
            </Box>

            <Spacer height="7" />

            <RichSelect
                isSearchVisible={true}
                label={translate('campagne_97871')}
                values={campaignsIds}
                onChange={setCampaignsIds}
            >
                {props.campaigns.map((campaign) => (
                    <option key={campaign.id} value={campaign.id}>
                        {campaign.name}
                    </option>
                ))}
            </RichSelect>

            {props.canCreate && (
                <>
                    <Spacer height="2" />

                    <Button
                        color="invisible"
                        leftIcon="plus"
                        onClick={() => {
                            const options = encodeBase64(
                                JSON.stringify({
                                    usersInfosIds: props.selecteds.ids
                                })
                            );

                            history.push(
                                CampaignsPaths.CREATE({
                                    organizationId: props.organizationId,
                                    eventId: props.eventId || undefined,
                                    options
                                })
                            );
                        }}
                    >
                        {translate('cr_er_une_nouve_86635')}
                    </Button>
                </>
            )}

            <Spacer height="7" />

            <Flex gap="3" justify="end">
                <Button disabled={!valid} isLoading={isMassCampaignSendLoading} onClick={onUpdate}>
                    {translate('envoyer_26609')}
                </Button>

                <Button color="white" onClick={props.onClose}>
                    {translate('annuler_48254')}
                </Button>
            </Flex>
        </Modal>
    );
};

interface IMassCampaignSendModalLoadingProps {
    eventId: Emptyable<EventId>;
    organizationId: OrganizationId;
    selecteds: MassActionSelectedsInput;

    onClose(): void;
    onSuccess(): void;
}

export const MassCampaignSendModalLoading = (props: IMassCampaignSendModalLoadingProps) => {
    const { data } = useMassCampaignSendCampaignsQuery({
        organizationId: props.organizationId,
        level: props.eventId ? OrganizationEventLevel.Event : OrganizationEventLevel.Organization,
        eventId: props.eventId
    });

    return (
        <MassCampaignSendModal
            campaigns={data.organization?.campaigns.nodes ?? []}
            canCreate={true}
            delegationId={null}
            eventId={props.eventId}
            organizationId={props.organizationId}
            selecteds={props.selecteds}
            onClose={props.onClose}
            onSuccess={props.onSuccess}
        />
    );
};
